import React, { FC, ReactNode, useCallback } from "react";
import classNames from "classnames/dedupe";
import { SvgIcon } from "../../Icons";
import { Typography } from "../../Fundamentals/Typography";

export type TThumbRating = -1 | 0 | 1;

export interface IThumbProps {
  classes?: {
    root?: string;
  };
  /** Current user's attitude */
  value: TThumbRating;
  /** Left button text (likes, yes, etc.) */
  leftButtonText?: ReactNode;
  /** Right button text (dislikes, no, etc.) */
  rightButtonText?: ReactNode;
  /** Will not fire any callbacks / will not have cursor-pointer, etc. */
  readonly?: boolean;
  /** Click on buttons callback */
  onChange?(newRating: TThumbRating): void;
}

const Thumb: FC<IThumbProps> = ({
  classes,
  value = 0,
  leftButtonText,
  rightButtonText,
  readonly = false,
  onChange,
}) => {
  const handleNegativeClick = useCallback(() => {
    !readonly && onChange && onChange(value === -1 ? 0 : -1);
  }, [onChange, value, readonly]);

  const handlePositiveClick = useCallback(() => {
    !readonly && onChange && onChange(value === 1 ? 0 : 1);
  }, [onChange, value, readonly]);

  const leftBtnClassname = classNames(
    `d-flex align-items-center me-6 ${
      value === 1 ? "text-color-red" : "text-color-blue"
    }`,
    {
      "cursor-pointer": !readonly,
    },
  );

  const rightBtnClassName = classNames(
    `d-flex align-items-center me-6 ${
      value === -1 ? "text-color-red" : "text-color-blue"
    }`,
    {
      "cursor-pointer": !readonly,
    },
  );

  return (
    <div
      className={
        classes?.root ?? "d-flex justify-content-between align-items-center"
      }
    >
      <div
        data-testid="Thumbs__pos"
        className={leftBtnClassname}
        onClick={handlePositiveClick}
      >
        <SvgIcon
          icon={value === 1 ? "ThumbUpActiveIcon" : "ThumbUpIcon"}
          data-testid={value === 1 ? "ThumbUpActiveIcon" : "ThumbUpIcon"}
        />
        {leftButtonText ? (
          <Typography variant="text-desc" classes={{ root: "ms-2" }}>
            {leftButtonText}
          </Typography>
        ) : null}
      </div>
      <div
        data-testid="Thumbs__neg"
        className={rightBtnClassName}
        onClick={handleNegativeClick}
      >
        <SvgIcon
          classes={{ root: "translate-rotate-180" }}
          icon={value === -1 ? "ThumbUpActiveIcon" : "ThumbUpIcon"}
          data-testid={value === -1 ? "ThumbUpActiveIcon" : "ThumbUpIcon"}
        />
        {rightButtonText ? (
          <Typography variant="text-desc" classes={{ root: "ms-2" }}>
            {rightButtonText}
          </Typography>
        ) : null}
      </div>
    </div>
  );
};

export default Thumb;
